import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import type { Session } from '../../api/session';
import { createPass, createPassRequest, getObject, getObjectAddresses, getPasses, getTemplates, type NewPass, type ObjectInfo } from '../../api/tenant';
import { passOptions, type GuestKind, type Mode } from '../../domain/availability';
import { formatServerDate } from '../../domain/moscowTime';
import { passesWord } from '../../domain/crew';
import { describePassError } from '../../domain/passErrors';
import { frequentGuests, GuestType } from '../../domain/passes';
import { normalizePlate } from '../../domain/plate';
import { regularDates, regularDayLabel, WEEKDAYS } from '../../domain/regular';
import { passWindow } from '../../domain/schedule';
import type { TelegramApi } from '../../telegram/webapp';
import { Chips, Segmented } from '../../ui/Choice';
import { Skeleton } from '../../ui/Skeleton';
import { MessageScreen } from '../auth/screens';
import { LoadFailed } from '../common/LoadFailed';
import s from './passForm.module.css';

interface Props {
  session: Session;
  telegram: TelegramApi;
  object: ObjectInfo;
}

type DayResult = { day: string; ok: boolean; error?: string };

/**
 * «Регулярный гость» (ideas 2026-10-13 #1, light version): a nanny or a cleaner on the same weekdays. v1 has no
 * schedule, so every chosen day of the next one or two weeks gets its own one-day pass in one go.
 */
export function RegularScreen({ session, telegram, object }: Props) {
  const queryClient = useQueryClient();
  const addresses = useQuery({ queryKey: ['addresses', object.id], queryFn: () => getObjectAddresses(session, object.id) });
  const details = useQuery({ queryKey: ['object', object.id], queryFn: () => getObject(session, object.id) });
  const templates = useQuery({ queryKey: ['templates'], queryFn: () => getTemplates(session) });
  const recent = useQuery({ queryKey: ['passes', 'recent'], queryFn: () => getPasses(session) });

  const opts = useMemo(() => passOptions(details.data?.settings ?? {}), [details.data]);
  const usable = useMemo(() => (addresses.data ?? []).filter((a) => !a.isBlocked), [addresses.data]);
  const guests = useMemo(() => frequentGuests(templates.data ?? [], recent.data ?? []), [templates.data, recent.data]);

  const [addressId, setAddressId] = useState<number | null>(null);
  const [guest, setGuest] = useState<GuestKind | null>(null);
  const [plate, setPlate] = useState('');
  const [name, setName] = useState('');
  const [days, setDays] = useState<number[]>([]);
  const [weeks, setWeeks] = useState<'1' | '2'>('2');
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<{ mode: Mode; days: DayResult[] } | null>(null);

  useEffect(() => {
    if (addressId === null && usable.length) setAddressId(usable[0].id);
  }, [usable, addressId]);
  useEffect(() => {
    if (guest === null && opts.guestTypes.length) setGuest(opts.guestTypes[0]);
  }, [opts, guest]);

  // A day-long pass: one-time first, a temporary pass that ends the same day otherwise.
  const plan = guest ? (opts.durations(guest).find((d) => d.duration === 'oneTime') ?? opts.durations(guest)[0] ?? null) : null;
  const limitKey = plan?.duration === 'temporary' ? 'passesLimitHoursTemporary' : 'passesLimitHoursOneTime';
  const limitRaw = details.data?.settings[limitKey];
  const limitHours = typeof limitRaw === 'number' && limitRaw > 0 ? limitRaw : null;
  const dates = regularDates(days, Number(weeks), new Date());

  const submit = useMutation({
    mutationFn: async () => {
      const out: DayResult[] = [];
      // One by one: a refusal for one day (a holiday limit) must not cost the other days their pass.
      for (const day of dates) {
        const window = passWindow({ when: { date: day }, duration: plan!.duration, limitHours, now: new Date(), forRequest: plan!.mode === 'request', until: day });
        const body: NewPass = {
          addressId: addressId!,
          durationType: plan!.duration === 'temporary' ? 2 : 1,
          guestType: guest === 'vehicle' ? GuestType.Vehicle : GuestType.Pedestrian,
          guestData: guest === 'vehicle' ? { plateNumber: plate } : { name: name.trim() },
          ...(window.startsAt ? { startsAt: formatServerDate(window.startsAt) } : {}),
          ...(window.expiresAt ? { expiresAt: formatServerDate(window.expiresAt) } : {}),
          ...(comment.trim() ? { comment: comment.trim() } : {}),
        };
        try {
          if (plan!.mode === 'request') await createPassRequest(session, body);
          else await createPass(session, body);
          out.push({ day, ok: true });
        } catch (e) {
          const d = describePassError(e);
          out.push({ day, ok: false, error: d.general ?? d.fields.plate ?? 'Не удалось оформить' });
        }
      }
      return out;
    },
    onSuccess: (out) => {
      telegram.notify(out.every((d) => d.ok) ? 'success' : 'error');
      void queryClient.invalidateQueries({ queryKey: ['passes'] });
      setResults({ mode: plan!.mode, days: out });
    },
  });

  const failed = addresses.isError || details.isError;
  if (failed) {
    return <LoadFailed error={addresses.error ?? details.error} onRetry={() => { void addresses.refetch(); void details.refetch(); }} fullScreen />;
  }
  if (!addresses.data || !details.data) {
    return (
      <main className={s.sheet} aria-busy="true">
        <Skeleton height={44} />
        <Skeleton height={120} />
      </main>
    );
  }
  if (!usable.length) {
    return <MessageScreen title="Заказ недоступен" text="Адрес заблокирован управляющей компанией. Позвоните в УК, чтобы узнать причину." />;
  }
  if (!opts.guestTypes.length) {
    return <MessageScreen title="Заказ недоступен" text="На этом объекте жители не оформляют пропуска сами. Обратитесь в УК." />;
  }

  if (results) {
    const ok = results.days.filter((d) => d.ok).length;
    return (
      <main className={s.done}>
        <h1 className={s.doneTitle}>
          {results.mode === 'request' ? 'Заявки отправлены' : ok === results.days.length ? 'Пропуска оформлены' : `Оформлено ${ok} из ${results.days.length}`}
        </h1>
        <p className={s.doneText}>{guest === 'vehicle' ? plate : name.trim()}</p>
        <ul className={s.crewResults} aria-label="Дни">
          {results.days.map((d) => (
            <li key={d.day} className={d.ok ? s.crewOk : s.crewFail}>
              <b>{regularDayLabel(d.day)}</b> {d.ok ? (results.mode === 'request' ? '— на согласовании в УК' : '— пропуск оформлен') : `— ${d.error}`}
            </li>
          ))}
        </ul>
        <Link to="/" className={s.submit}>
          На главную
        </Link>
      </main>
    );
  }

  const pickGuest = (key: string) => {
    const g = guests.find((x) => x.key === key);
    if (!g) return;
    if (g.source.kind === 'template') {
      const t = templates.data?.find((x) => x.id === g.source.id);
      if (!t) return;
      if (t.guestType === GuestType.Pedestrian) {
        setGuest('pedestrian');
        setName(t.guestData?.name ?? '');
      } else {
        setGuest('vehicle');
        setPlate(normalizePlate(t.guestData?.plateNumber ?? ''));
      }
    } else if (g.plate) {
      setGuest('vehicle');
      setPlate(normalizePlate(g.plate));
    } else {
      setGuest('pedestrian');
      setName(key.startsWith('pedestrian:') ? key.slice('pedestrian:'.length) : g.label);
    }
  };
  const toggleDay = (d: number) => setDays((list) => (list.includes(d) ? list.filter((x) => x !== d) : [...list, d].sort()));
  const onSubmit = () => {
    if (guest === 'vehicle' && plate.length < 3) return setError('Введите госномер');
    if (guest === 'pedestrian' && !name.trim()) return setError('Введите имя гостя');
    if (!dates.length) return setError('Выберите дни недели');
    if (!plan) return setError('Такие пропуска на этом объекте недоступны');
    setError(null);
    submit.mutate();
  };

  return (
    <main className={s.sheet} aria-labelledby="regular-title">
      <header className={s.head}>
        <h1 id="regular-title" className={s.title}>
          Регулярный гость
        </h1>
        <p className={s.where}>Няня, уборка, садовник — пропуска на нужные дни вперёд</p>
      </header>

      {addresses.data.length > 1 && (
        <Chips
          label="Адрес"
          value={addressId}
          items={addresses.data.map((a) => ({ id: a.id, label: a.isBlocked ? `${a.name} — заблокирован` : a.name, disabled: a.isBlocked }))}
          onChange={(v) => v !== null && setAddressId(v)}
        />
      )}

      {guests.length > 0 && (
        <div className={s.field}>
          <span className={s.label}>Частые гости</span>
          <Chips label="Частые гости" value={null} items={guests.map((g) => ({ id: g.key, label: g.label || g.plate || 'Гость' }))} onChange={(v) => v && pickGuest(v)} limit={5} />
        </div>
      )}

      {opts.guestTypes.length > 1 && (
        <Segmented
          label="Как приезжает"
          value={guest ?? opts.guestTypes[0]}
          items={opts.guestTypes.map((g) => ({ id: g, label: g === 'vehicle' ? 'На машине' : 'Пешком' }))}
          onChange={setGuest}
        />
      )}

      {guest === 'vehicle' ? (
        <div className={s.field}>
          <label htmlFor="regular-plate" className={s.label}>
            Госномер
          </label>
          <input
            id="regular-plate"
            className={s.plate}
            value={plate}
            onChange={(e) => setPlate(normalizePlate(e.target.value))}
            placeholder="А000АА77"
            autoComplete="off"
            autoCapitalize="characters"
          />
        </div>
      ) : (
        <div className={s.field}>
          <label htmlFor="regular-name" className={s.label}>
            Имя гостя
          </label>
          <input id="regular-name" className={s.input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Например, Мария — няня" />
        </div>
      )}

      <div className={s.field}>
        <span className={s.label}>По каким дням</span>
        <div role="group" aria-label="Дни недели" className={s.weekdays}>
          {WEEKDAYS.map((label, i) => (
            <button key={label} type="button" aria-pressed={days.includes(i)} className={days.includes(i) ? `${s.weekday} ${s.weekdayOn}` : s.weekday} onClick={() => toggleDay(i)}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <Segmented
        label="На сколько недель"
        value={weeks}
        items={[
          { id: '1', label: 'Неделя' },
          { id: '2', label: 'Две недели' },
        ]}
        onChange={setWeeks}
      />
      <p className={s.caption}>{dates.length ? `Пропуска на весь день: ${dates.map(regularDayLabel).join(', ')}` : 'Выберите дни — покажем даты'}</p>

      <div className={s.field}>
        <label htmlFor="regular-comment" className={s.label}>
          Комментарий для охраны · необязательно
        </label>
        <textarea id="regular-comment" className={s.textarea} value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Например, няня, к 9 утра" />
      </div>

      <div className={s.footer}>
        {error && (
          <p className={s.error} role="alert">
            {error}
          </p>
        )}
        <button type="button" className={s.submit} onClick={onSubmit} disabled={submit.isPending}>
          {submit.isPending
            ? 'Оформляем…'
            : plan?.mode === 'request'
              ? `Отправить на согласование${dates.length > 1 ? ` (${dates.length})` : ''}`
              : dates.length > 1
                ? `Оформить ${dates.length} ${passesWord(dates.length)}`
                : 'Оформить пропуск'}
        </button>
      </div>
    </main>
  );
}
